import { Divider, Stack, Typography } from '@mui/material';
import {
  Provider,
  BackupPolicyType,
  ScheduleMode,
  type BackupSchedule,
} from '@repo/api-gw-sdk';

import { TimeSelectorPopover } from '@/components/date/timeSelectorPopover';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type {
  OptionMenuItemType,
  OptionType,
} from '@/components/option-menu/option-menu';
import { Schedule, ScheduleContainer } from '@/components/schedule/schedule';
import { AllRegionsSelector } from '@/components/selectors/allRegionsSelector';
import { BackupVaultTagSelection } from '@/components/vaults/backupVaultTagSelection';
import { dayjs } from '@/utils/dayjs';

import { isNativePolicyType } from './backupPoliciesTypes';

import { DayOfMonthSelector } from '../controls/dayOfMonthSelector';
import { DayOfWeekSelector } from '../controls/dayOfWeekSelector';
import { DaysOfYearSelector } from '../controls/daysOfYearSelector';
import { FrequencyWindowSelector } from '../controls/frequencyWindowSelector';
import { HoursSelector } from '../controls/hoursSelector';
import { RetentionPopover } from '../controls/retentionSelectionPopover';
import {
  annuallyFrequency,
  collectDaysAndMonths,
  dailyFrequency,
  deconstructCron,
  getFrequencyByWindow,
  getHoursDisplay,
  getWindowDurationDisplay,
  getWindowHoursDuration,
  lessThanDayOptions,
  monthlyFrequency,
  weeklyFrequency,
  type Frequency,
} from '../frequency';

const MAX_RETENTION_DAYS_FOR_AWS_NATIVE_PITR = 35;
const MIN_RETENTION_DAYS_FOR_AWS_NATIVE_PITR = 1;
export const isValidSchedule = (
  schedule: BackupSchedule,
  policyType: BackupPolicyType
) => {
  const retentionInDays = schedule.backupRetention;
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR) {
    return (
      retentionInDays <= MAX_RETENTION_DAYS_FOR_AWS_NATIVE_PITR &&
      retentionInDays >= MIN_RETENTION_DAYS_FOR_AWS_NATIVE_PITR
    );
  }

  if (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD &&
    !schedule.region
  ) {
    return false;
  }

  if (!isNativePolicyType(policyType) && !schedule.vaultId) {
    return false;
  }

  const frequency = getFrequencyByWindow(schedule.windows);
  if (!frequency) {
    return false;
  }

  if (retentionInDays < frequency.minimumRetentionInDays) {
    return false;
  }

  return true;
};

export function BackupPolicySchedule(props: {
  schedule: BackupSchedule;
  deleteAllowed?: boolean;
  onUpdate: (schedule: BackupSchedule) => void;
  onDuplicate?: (schedule: BackupSchedule) => void;
  onDelete?: (schedule: BackupSchedule) => void;
  mode: ScheduleMode;
  policyType: BackupPolicyType;
}) {
  const onUpdatedRetention = (retentionInDays: number) => {
    props.onUpdate({
      ...props.schedule,
      backupRetention: retentionInDays,
    });
  };

  const frequency = getFrequencyByWindow(props.schedule.windows);

  const isNative = isNativePolicyType(props.policyType);
  const isContinuous =
    props.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR;
  const destinationText =
    props.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD
      ? 'to the source account in'
      : props.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR
        ? 'to the source account'
        : 'on';

  return (
    <ScheduleContainer>
      <Schedule direction='row' alignItems='center' gap='16px'>
        <Stack gap='12px'>
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Back up</Typography>
            {isContinuous ? (
              <Typography className='px-[8px] font-medium'>
                continuously
              </Typography>
            ) : (
              <FrequencyWindowSelector
                schedule={props.schedule}
                onUpdate={props.onUpdate}
                policyType={props.policyType}
              />
            )}
            <Typography>and retain for</Typography>
            <RetentionPopover
              onUpdate={onUpdatedRetention}
              retentionInDays={props.schedule.backupRetention}
              defaultRetentionInDays={frequency?.defaultRetentionInDays}
              minimumRetentionInDays={
                isContinuous
                  ? MIN_RETENTION_DAYS_FOR_AWS_NATIVE_PITR
                  : frequency?.minimumRetentionInDays
              }
              maximumRetentionInDays={
                isContinuous
                  ? MAX_RETENTION_DAYS_FOR_AWS_NATIVE_PITR
                  : undefined
              }
            />
            <Typography>{destinationText}</Typography>
            {props.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD && (
              <Stack width='max-content'>
                <AllRegionsSelector
                  size='small'
                  variant='standard'
                  setRegionName={(region) =>
                    props.onUpdate({ ...props.schedule, region })
                  }
                  regionName={props.schedule.region}
                  provider={Provider.Aws}
                />
              </Stack>
            )}
            {!isNative && (
              <BackupVaultTagSelection
                vaultId={props.schedule.vaultId}
                onUpdate={(vault) =>
                  props.onUpdate({ ...props.schedule, vaultId: vault.id })
                }
                entityName={
                  getFrequencyByWindow(
                    props.schedule.windows
                  )?.text?.toLowerCase() || ''
                }
              />
            )}
          </Stack>
          {props.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED && (
            <AdvancedSchedule
              schedule={props.schedule}
              onUpdate={props.onUpdate}
            />
          )}
        </Stack>
        {props.onDuplicate && props.onDelete && (
          <OptionMenu
            options={[
              {
                id: 'duplicate-backup-policy',
                text: 'Duplicate',
              },
              {
                id: 'delete-backup-policy',
                text: 'Delete',
                menuItemProps: { disabled: !props.deleteAllowed },
              },
            ]}
            onOptionSelected={(option: OptionType): void => {
              switch ((option as OptionMenuItemType).text) {
                case 'Duplicate':
                  props.onDuplicate?.(props.schedule);
                  break;
                case 'Delete':
                  props.onDelete?.(props.schedule);
                  break;
              }
            }}
          />
        )}
      </Schedule>
    </ScheduleContainer>
  );
}

const AdvancedSchedule = ({
  schedule,
  onUpdate,
}: {
  schedule: BackupSchedule;
  onUpdate: (schedule: BackupSchedule) => void;
}) => {
  const frequency = getFrequencyByWindow(schedule.windows);
  if (!frequency) {
    return null;
  }

  const {
    minutes: minutesStr,
    hours: hoursStr,
    dayOfMonth,
    month,
    dayOfWeek,
  } = deconstructCron(schedule.windows);

  const minutes = parseInt(minutesStr);
  const hours = parseInt(hoursStr);

  return (
    <>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Stack direction='row' alignItems='center' gap='8px'>
        {frequency === weeklyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for</Typography>
            <DayOfWeekSelector
              days={dayOfWeek.split(',').map((x) => parseInt(x))}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    dayOfMonth,
                    month,
                    days.join(','),
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {frequency === monthlyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for the</Typography>
            <DayOfMonthSelector
              days={dayOfMonth.split(',')}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    days.join(','),
                    month,
                    dayOfWeek,
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {frequency === annuallyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for</Typography>
            <DaysOfYearSelector
              title='Select at least one backup date for each year.'
              days={collectDaysAndMonths(schedule.windows).map((x) => ({
                ...x,
                year: dayjs.utc().year(),
              }))}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    days.map((x) => x.day).join(','),
                    days.map((x) => x.month).join(','),
                    dayOfWeek,
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {!lessThanDayOptions.some((x) => x === frequency) && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>
              {frequency === dailyFrequency ? 'Schedule for' : 'at'}
            </Typography>
            <TimeSelectorPopover
              hours={isNaN(hours) ? 0 : hours}
              minutes={isNaN(minutes) ? 0 : minutes}
              onChange={(hours, minutes) =>
                onUpdate({
                  ...schedule,
                  windows: [minutes, hours, dayOfMonth, month, dayOfWeek].join(
                    ' '
                  ),
                })
              }
            />
            <Typography>and start within</Typography>
            <HoursSelector
              hours={schedule.duration / 60}
              onChange={(hours) =>
                onUpdate({
                  ...schedule,
                  duration: hours * 60,
                })
              }
            />
          </Stack>
        )}
        {lessThanDayOptions.some((x) => x === frequency) && (
          <AdvancedHighFrequency frequency={frequency} />
        )}
      </Stack>
    </>
  );
};

const AdvancedHighFrequency = ({ frequency }: { frequency: Frequency }) => {
  const { hours, minutes } = deconstructCron(frequency.windows);
  const durationInHours = getWindowHoursDuration(frequency);
  const durationDisplay = getWindowDurationDisplay(durationInHours);

  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      <Typography>{`Scheduled for ${getHoursDisplay(hours, minutes)}`}</Typography>
      <Typography
        variant='subtitle2'
        color='var(--mui-palette-text-secondary)'
        sx={{
          border: '1px solid var(--mui-palette-divider)',
          padding: '6px 12px',
          borderRadius: '4px',
        }}
      >
        {`Backups start within ${durationDisplay} of the scheduled times`}
      </Typography>
    </Stack>
  );
};
